import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, List, ListItem, ListItemText,
  ListItemIcon, ListItemSecondaryAction, Divider, Chip, Tooltip,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ShareIcon from '@mui/icons-material/Share';
import CommentIcon from '@mui/icons-material/Comment';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { dmsApi } from '../services/dmsApi';
import { useSnackbar } from '../contexts/SnackbarContext';

const TYPE_CONFIG = {
  workflow_approved: { icon: <CheckCircleIcon color="success" />, color: 'success' },
  workflow_rejected: { icon: <CancelIcon color="error" />, color: 'error' },
  workflow_assigned: { icon: <CheckCircleIcon color="warning" />, color: 'warning' },
  document_shared: { icon: <ShareIcon color="info" />, color: 'info' },
  comment_added: { icon: <CommentIcon color="primary" />, color: 'primary' },
  document_expiring: { icon: <WarningIcon color="warning" />, color: 'warning' },
  legal_hold: { icon: <WarningIcon color="error" />, color: 'error' },
  classification_changed: { icon: <CheckCircleIcon color="info" />, color: 'info' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

const NotificationCenter = ({ onNavigateToDoc }) => {
  const { showSnack } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dmsApi.notifications.list(false, 25);
      setNotifications(res.notifications || res.data?.notifications || []);
      setUnreadCount(res.unreadCount ?? res.data?.unreadCount ?? 0);
    } catch {
      // Silently fail — notifications are not critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await dmsApi.notifications.markRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {} // eslint-disable-line no-empty
  };

  const handleMarkAllRead = async () => {
    try {
      await dmsApi.notifications.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      showSnack('All notifications marked as read', 'success');
    } catch {} // eslint-disable-line no-empty
  };

  const handleDelete = async (id) => {
    try {
      await dmsApi.notifications.delete(id);
      const wasUnread = notifications.find(n => n._id === id && !n.read);
      setNotifications(prev => prev.filter(n => n._id !== id));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {} // eslint-disable-line no-empty
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={e => { setAnchorEl(e.currentTarget); fetchNotifications(); }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 520, borderRadius: 2, overflow: 'hidden' } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications{unreadCount > 0 && (
              <Chip label={unreadCount} color="error" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
            )}
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={handleMarkAllRead}><DoneAllIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Box>

        {/* List */}
        <Box sx={{ overflow: 'auto', maxHeight: 420 }}>
          {loading && notifications.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.4 }} />
              <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No notifications yet</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notif, idx) => {
                const typeConfig = TYPE_CONFIG[notif.type] || { icon: <NotificationsIcon />, color: 'default' };
                return (
                  <React.Fragment key={notif._id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        bgcolor: notif.read ? 'transparent' : 'action.hover',
                        cursor: notif.documentId ? 'pointer' : 'default',
                        '&:hover': { bgcolor: 'action.selected' },
                        pr: 6,
                      }}
                      onClick={() => {
                        if (!notif.read) handleMarkRead(notif._id);
                        if (notif.documentId && onNavigateToDoc) onNavigateToDoc(notif.documentId);
                        setAnchorEl(null);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5, position: 'relative' }}>
                        {!notif.read && (
                          <Box sx={{
                            width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main',
                            position: 'absolute', left: -8, top: '50%', transform: 'translateY(-50%)',
                          }} />
                        )}
                        {typeConfig.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={notif.read ? 400 : 700}>
                            {notif.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">{notif.message}</Typography>
                            <br />
                            <Typography variant="caption" color="text.disabled">{timeAgo(notif.createdAt)}</Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={e => { e.stopPropagation(); handleDelete(notif._id); }}
                        >
                          <DeleteIcon fontSize="small" sx={{ opacity: 0.4 }} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {idx < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter;
